/* Copyright (c) 2026 Xperi. All Rights Reserved.  */

import type { TSessionApiVersion } from "@tivocorp/data-domain-lib";

export const DEFAULT_APPLICATION_NAME = "cablecotmis01";

// These mappings will be updated once deviceType is onboarded
export const SUPPORTED_DEVICE_TYPES = {
  IPTV_SMART_TV: "iptvSmartTv",
  ROKU_TV: "iptvSmartTv",
  VEGA_OS: "iptvSmartTv",
  WEB_PLAYER_CHROME: "webPlayer",
  WEB_PLAYER_SAFARI: "webPlayerSafari",
  WEB_PLAYER: "webPlayer"
};

export const DEFAULT_DEVICE_TYPE = SUPPORTED_DEVICE_TYPES.IPTV_SMART_TV;

export const DEFAULT_MSO_NAME = "cablecotmis01";

export const DEFAULT_LOCALE = "en_US";

export const DEFAULT_LANGUAGE = "en";

export const MILLISECONDS_IN_A_SECOND = 1000;

export const SECONDS_IN_A_MINUTE = 60;

export const MILLISECONDS_PER_MINUTE: number =
  SECONDS_IN_A_MINUTE * MILLISECONDS_IN_A_SECOND;

export const DEFAULT_CAROUSELS_REFRESH_INTERVAL_SECS = 120;

export const TOAST_AUTO_HIDE_DURATION_MS = 5000;

export const PARTNER_BRANDING_LOGO_WIDTH_1080P = 600;

export const PARTNER_BRANDING_LOGO_HEIGHT_1080P = 360;

export const PRIMARY_BRANDING_LOGO_WIDTH_1080P = 165;

export const PRIMARY_BRANDING_LOGO_HEIGHT_1080P = 48;

export const IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD = 12000 * 1000; // 12000 seconds

export const SESSION_MANAGER_GRACE_PERIOD = 4 * 60 * 60 * 1000; // 4 hours

// Session Manager API version used for sessionCreate. keepAlive and delete
// follow the version the session was created with.
export const SESSION_MANAGER_API_VERSION: TSessionApiVersion = "v2";

// Max time the app may stay backgrounded before a return to foreground is
// treated as a "long background" and triggers a playback re-sync.
export const APP_BACKGROUND_MAX_MS = 30 * 60 * 1000; // 30 minutes

export const SUPPORTED_LOCALES = [
  { language: "en", locale: "en_US" },
  { language: "es", locale: "es_US" },
  { language: "fr", locale: "fr_CA" }
];

export const TSRI_BASE_URL = "https://tsri.dev.tivoservice.com";

export const TSRI_ID = "integration";

/**
 * Per-attempt watchdog timeout for the IP auto-login token request (ms).
 * Matches the cross-platform watchdogTimeoutSeconds default.
 */
export const IP_AUTO_LOGIN_ATTEMPT_TIMEOUT_MS: number = 5000;

/**
 * Total budget for the IP auto-login token request including retries (ms).
 * Prevents the splash screen from hanging indefinitely.
 */
export const IP_AUTO_LOGIN_TOTAL_TIMEOUT_MS: number = 15000;

/**
 * TSRI catalog service names for every implemented feature, pre- and
 * post-sign-in. Each key is a PascalCase logical name; each value is the exact
 * TSRI service-name string used for endpoint resolution.
 *
 * The subset that must be resolvable *before* sign-in is listed separately in
 * {@link REQUIRED_PRE_SIGN_IN_SERVICES}.
 */
export const TIVO_SERVICES = {
  ActionService: "cloudcore-actions",
  AiringOfferService: "cloudcore-airing",
  AuthConfigService: "auth-config-service",
  BrandingService: "cloudcore-branding-service",
  BookmarksService: "cloudcore-bookmarks",
  ChannelLogoImageHost: "channel-logo-image-host",
  ChannelSettingService: "cloudcore-channel-settings",
  ChannelsIptv: "channels-iptv",
  CloudcoreInHomeLocation: "cloudcore-inhomelocation",
  CloudcoreRating: "cloudcore-rating",
  ConnectedService: "connected",
  DeviceBindingService: "devicebindingservice",
  DeviceConfigService: "device-config-service",
  DeviceProvisionService: "device-provision-service",
  DomainTokens: "domain-tokens",
  EpisodeGuideService: "episode-guide",
  FeatureProvisioningGateway: "fe-feature-provisioning-gateway",
  FilteredGuideService: "filtered-guide",
  Guide: "cloudcore-guide",
  HttpsInsecureXff: "httpsinsecurexff",
  ImageHost: "image-host",
  MyShowsService: "cloudcore-myshows",
  OnePassService: "cloudcore-onepass",
  PreviewService: "cloudcore-previews",
  ProfileManagementService: "profile-management-service",
  QuickMenu: "quick-menu",
  RecordingsService: "cloudcore-recordings",
  RecordingsStatusService: "guide-recordings-status",
  SageAnalytics: "analyticssecure",
  SageDiagnostics: "sagediagsecure",
  SageSecure: "sagesecure",
  SessionManagerService: "session-manager-lambda",
  ShowflixCarousel: "showflix-carousels",
  SmartCarousel: "smart-carousel",
  SmartSearch: "smart-search",
  SoftClientCert: "softclient-cert",
  TivoIdentityAuthService: "tivo-identity-auth-service",
  UpcomingOffersService: "upcoming-offers",
  VideoProviderConfigurationsService: "cloudcore-provider-config",
  VodOttActionDetails: "cc-vod-ott-action-details",
  VodSocuLastViewedPositionService: "vod-socu-last-viewed-position",
  WatchHistoryService: "watch-history",
  WsSecure: "wssecure"
};

/**
 * Services that must be present in the TSRI endpoints response for the
 * app to reach the sign-in flow. A response missing any of these is treated as
 * partial and fails startup — so this stays a strict subset of
 * {@link TIVO_SERVICES}, never the whole catalog.
 */
export const REQUIRED_PRE_SIGN_IN_SERVICES: string[] = [
  TIVO_SERVICES.AuthConfigService,
  TIVO_SERVICES.DeviceConfigService,
  TIVO_SERVICES.DomainTokens,
  TIVO_SERVICES.DeviceBindingService,
  TIVO_SERVICES.DeviceProvisionService,
  TIVO_SERVICES.TivoIdentityAuthService
];

/**
 * The blocking tier for endpoint recovery.
 *
 * `REQUIRED_PRE_SIGN_IN_SERVICES` is checked once, against the startup TSRI
 * response. This list is checked at every *lookup*, for the whole session: when
 * a service named here is absent from the endpoint cache, `TsriEndpointResolver`
 * raises the alert overlay and re-fetches the catalog in the background until
 * the service appears or the INTERACTIVE budget (~35s) runs out.
 *
 * A service **not** listed here degrades instead: the re-fetch still runs, but
 * silently, and the one feature that needed the endpoint does without it. That
 * is the whole point of the tier — an absent `smart-carousel` must not freeze
 * the app behind an overlay.
 *
 * TODO(UAW-2757): seeded with the pre-sign-in set, which is the only tier this
 * codebase can justify on its own. Replace with the product-defined criticality
 * list — this constant is the single place that decides what blocks.
 */
export const CRITICAL_TSRI_SERVICES: string[] = [
  ...REQUIRED_PRE_SIGN_IN_SERVICES
];

export const TIZEN_PLATFORM = "Tizen";

/** Duration of the guide window in minutes (90 minutes = 1.5 hours). */
export const GUIDE_WINDOW_DURATION_MINUTES = 90;

/** Interval for each time slot in the guide header (30 minutes = half hour). */
export const GUIDE_TIME_SLOT_INTERVAL_MINUTES = 30;

/** Number of channel rows visible on screen at once in the EPG grid. */
export const GUIDE_VISIBLE_ROW_COUNT: number = 6;

/** Highest vertical position the highlight can occupy (0-indexed, inclusive). */
export const GUIDE_HIGHLIGHT_ROW_MIN: number = 2;

/** Lowest vertical position the highlight can occupy (0-indexed, inclusive). */
export const GUIDE_HIGHLIGHT_ROW_MAX: number = 3;

/** Minutes shifted per single time-axis step (left or right at boundary). */
export const GUIDE_SHIFT_MINUTES: number = 30;

/** Milliseconds per single time-axis step (30 minutes in ms). */
export const GUIDE_SHIFT_MS: number =
  GUIDE_SHIFT_MINUTES * MILLISECONDS_PER_MINUTE;

/** Maximum days forward the live/upcoming guide allows navigation. */
export const GUIDE_MAX_FUTURE_DAYS: number = 14;

/** Default EPG look-back hours when the branding bundle is unavailable. */
export const DEFAULT_EPG_LOOK_BACK_HOURS: number = 24 * 7; // 7 days

/**
 * Default EAS OSD display mode when the branding bundle is unavailable.
 * Resolves to the United States template (top of screen).
 */
export const DEFAULT_EAS_DISPLAY_MODE: string = "us";

/** Delay (ms) before expanding the filter sidebar. */
export const GUIDE_FILTER_SIDEBAR_EXPAND_DELAY_MS: number = 500;

/**
 * Maximum number of filter items visible in the guide filter sidebar at once.
 * Further items are reached by scrolling (focus past the edge, or the mouse
 * wheel). Also caps the range used when mapping the grid row to a filter on
 * entering the sidebar.
 */
export const GUIDE_FILTER_SIDEBAR_MAX_VISIBLE_ITEMS: number = 7;

/** Duration (ms) of the directional lock after entering the filter sidebar. */
export const GUIDE_SIDEBAR_DIRECTIONAL_LOCK_MS: number = 200;

/**
 * Quiet period (ms) after the last guide filter toggle before the apply
 * side effects (persist, category resolution, EPG cache clear, anchor,
 * window reload) actually run. Collapses a burst of multi-select taps in
 * the expanded filter overlay into a single settled apply instead of one
 * per tap.
 */
export const GUIDE_FILTER_APPLY_DEBOUNCE_MS: number = 300;

/**
 * How long a guide cell may stay in the loading state before it falls back to
 * the navigable "TBA" cell. Tunable without touching the implementation.
 */
export const GUIDE_LOADING_CELL_TIMEOUT_MS: number = 15000;

// ─── Guide sliding-window cache (UAW-570) ───
// The guide keeps a fixed in-memory window of channels and program data while
// channel metadata itself remains persisted in IndexedDB (CHANNELS).

/**
 * Total channels held in the in-memory guide window: top buffer + visible +
 * bottom buffer. Sized to fit a single guide-rows API request.
 */
export const GUIDE_TOTAL_CACHED_CHANNELS: number = 15;

/** Maximum stations allowed in a single guide-rows request. */
export const GUIDE_MAX_STATIONS_PER_REQ: number = 15;

/**
 * Number of channels requested per guide-rows API batch — both on initial
 * Guide screen entry and for each incremental scroll page load.
 * [REQ-GUIDEBATCH-FUN-011], [REQ-GUIDEBATCH-FUN-012]
 */
export const GUIDE_BATCH_SIZE: number = 8;

/**
 * Channels buffered above the visible window (smaller — users scroll up less).
 * 4 top + GUIDE_VISIBLE_ROW_COUNT (6) visible + 5 bottom = 15.
 */
export const GUIDE_CHANNEL_BUFFER_TOP: number = 4;

/** Channels buffered below the visible window (larger — users scroll down more). */
export const GUIDE_CHANNEL_BUFFER_BOTTOM: number = 5;

/** Remaining buffer channels (in the move direction) that trigger a channel prefetch. */
export const GUIDE_PREFETCH_CH_THRESHOLD: number = 1;

/** Channels fetched to restore the bottom buffer when scrolling down. */
export const GUIDE_PREFETCH_CH_DOWN_COUNT: number = 5;

/** Channels fetched to restore the top buffer when scrolling up. */
export const GUIDE_PREFETCH_CH_UP_COUNT: number = 4;

/** Zero-based start channel row under the highlight on initial load. */
export const GUIDE_START_CHANNEL_ROW: number = 2;

/** Time buffer slots cached on each side of the visible window (3 × 30min = 90min). */
export const GUIDE_TIME_BUFFER_SLOTS: number = 3;

/** Maximum span, in minutes, of a single guide-rows request (4 hours). */
export const GUIDE_MAX_FETCH_WINDOW_MINUTES: number = 240;

/** Remaining buffer slots (in the move direction) that trigger a time prefetch. */
export const GUIDE_PREFETCH_TIME_THRESHOLD_SLOTS: number = 1;

/** Time fetched per horizontal prefetch, in minutes (2 hours). */
export const GUIDE_PREFETCH_TIME_CHUNK_MINUTES: number = 120;

/**
 * How far into the future the cache is pre-warmed after the initial guide load,
 * in minutes (4 hours). This runs sequentially after the visible window has been
 * rendered so it never delays the first paint. [REQ-GUIDECACHE-PREFETCH-001]
 */
export const GUIDE_INITIAL_FUTURE_PREFETCH_MINUTES: number = 240;

/** Maximum cached time span before time-axis eviction, in minutes (8 hours). */
export const GUIDE_MAX_CACHED_TIME_MINUTES: number = 480;

/** Maximum number of digits accepted for jump-to-channel entry. */
export const GUIDE_JUMP_MAX_DIGITS: number = 5;

/** Inactivity timeout (ms) after the last digit before auto-commit. */
export const GUIDE_JUMP_ENTRY_TIMEOUT_MS: number = 1500;

/** Duration (ms) the "Not found" error state is displayed before auto-dismiss. */
export const GUIDE_JUMP_ERROR_DISPLAY_MS: number = 1500;

/**
 * Quiet period (ms) after the last directional navigation before the guide
 * settles (reconciles Redux from the cache). Debounced per-navigation so a
 * burst of fast discrete presses — which never engage turbo, since native
 * key-repeat never fires — still settles once the burst stops, the same way
 * a turbo (held-key) scroll settles on release.
 */
export const GUIDE_NAV_SETTLE_DEBOUNCE_MS: number = 200;

/** Refresh interval for the guide timebar, in milliseconds. */
export const GRID_TIMEBAR_REFRESH_TIME = 1 * MILLISECONDS_IN_A_SECOND;

/** Refresh interval for the guide preview progress, in milliseconds. */
export const GRID_PREVIEW_PROGRESS_REFRESH_TIME = 30 * MILLISECONDS_IN_A_SECOND;

/**
 * Auto-refresh interval for the guide grid (ms).
 * The timer fires when wall-clock time crosses the next 30-minute boundary,
 * advancing the visible window to keep the EPG current without user interaction.
 */
export const GUIDE_AUTO_REFRESH_INTERVAL_MS: number =
  30 * MILLISECONDS_PER_MINUTE;

/** TTL in seconds for the claim JWT (default 30 minutes). */
export const CLAIM_JWT_TTL_SECONDS: number = 1800;

/**
 * Seconds by which `nbf`/`iat` are backdated when signing device-signed JWTs.
 *
 * The device clock can run ahead of the services that validate these tokens,
 * which reject `nbf` in their own future strictly and with no leeway
 * ("token is not valid yet"). Stamping `nbf` at exactly `now` therefore makes
 * the first call with a freshly minted token fail whenever the device clock is
 * even slightly fast; it only recovers because the retry delay outlasts the
 * skew. Backdating absorbs that skew. `exp` stays anchored to the true `now`,
 * so a token's usable lifetime is unchanged.
 */
export const JWT_CLOCK_SKEW_BACKDATE_SECONDS: number = 10 * 60;
/** Maximum entries retained in the navigation stack before pruning. */
export const NAVIGATION_MAX_STACK_SIZE = 50;

/** Dwell threshold (ms) before a focused tile fires `contentHighlighted`. */
export const CONTENT_HIGHLIGHT_DWELL_MS: number = 3000;

export const SEASON_FOCUS_FETCH_DEBOUNCE_MS: number = 0;

/** Idle time before the OSD begins periodically repositioning to prevent screen burn-in (1 minute). */
export const OSD_STILL_TIME = 60 * MILLISECONDS_IN_A_SECOND;

/** Interval between OSD burn-in-prevention repositions once idle (5 seconds). */
export const OSD_MOVEMENT_TIME = 5 * MILLISECONDS_IN_A_SECOND;

/**
 * Idle time before the Player inactivity  OSD is shown on the
 */
export const PLAYBACK_INACTIVITY_OSD_TIMEOUT_MS =
  (3 * 60 + 58) * 60 * MILLISECONDS_IN_A_SECOND;

/**
 * Idle time after the inactivity OSD is shown before playback auto-exits to previous screen (2 minutes).
 */
export const PLAYBACK_INACTIVITY_EXIT_TIMEOUT_MS =
  2 * 60 * MILLISECONDS_IN_A_SECOND;

/**
 * Priority passed to the shared `osd` slice for the playback inactivity OSD.
 *
 * Must equal `ErrorPriority.STILL_THERE` so the inactivity prompt arbitrates on
 * the same ladder as real errors — it loses to every one of them (900 < 994),
 * but beats an un-prioritised (0) request. The value is inlined rather than
 * imported because `config.ts` is the lowest layer and must not depend on
 * `model/`; `config.test.ts` asserts the two stay in step.
 */
export const PLAYBACK_INACTIVITY_OSD_PRIORITY = 900;

/**
 * Priority passed to the shared `osd` slice for the Emergency Alert System
 * (EAS) OSD.
 *
 * Must equal `ErrorPriority.EAS_ALERT` — the highest value on the ladder — so
 * an EAS alert always outranks every other OSD/dialog request, including
 * `NO_NETWORK_ERROR`, per the EAS OSD specification. Inlined for the same
 * layering reason as `PLAYBACK_INACTIVITY_OSD_PRIORITY`; `config.test.ts`
 * asserts the two stay in step.
 */
export const EAS_OSD_PRIORITY = 1000;

/**
 * Upper bound on how long an Emergency Alert System (EAS) alert may hold the
 * screen when it carries no end signal of its own (2 minutes).
 *
 * The EasAlert schema declares no TTL, duration or expiry field — only `text`,
 * `mediaUrl`, `audioVideoContent`, `allowUserExit` and `alertType` — so an
 * alert that is neither dismissible (`allowUserExit: false`) nor media-bearing
 * (no `mediaUrl`, hence no end-of-media event) has nothing that can ever end
 * it. Without this bound such an alert holds every key dead until the app is
 * restarted, because `EasInputGate` blocks all input for its lifetime.
 *
 * Deliberately armed for that combination only. An alert the viewer can
 * dismiss, or one whose media reports end-of-media, already has a defined way
 * out and must not be cut short by a client-side clock — the server decides
 * how long a genuine emergency stays up. Replace this with the wire value if
 * the schema ever gains a TTL field.
 */
export const EAS_ALERT_MAX_DURATION_MS = 2 * MILLISECONDS_PER_MINUTE;

/**
 * Delay between an Emergency Alert System (EAS) alert coming off screen and
 * the content it interrupted being resumed.
 *
 * The alert's own media is torn down and the overlay removed in the same tick
 * the alert ends. Resuming in that tick puts the programme's audio back while
 * the alert tone is still unwinding on its own element and the overlay is
 * still painted, which reads as the two overlapping. A short beat separates
 * them, and is short enough that the viewer does not experience it as a stall.
 */
export const EAS_PLAYBACK_RESUME_DELAY_MS = 500;

/**
 * Continuous paused time, on non-live playback (SOCU / nDVR / VOD), before
 * the pause-timeout warning dialog is shown (9 minutes).
 */
export const PAUSE_TIMEOUT_WARNING_MS = 9 * MILLISECONDS_PER_MINUTE;

/**
 * Additional paused time after the warning dialog is shown before playback
 * auto-stops with no action (1 minute — 10 minutes paused in total).
 */
export const PAUSE_TIMEOUT_END_MS = 1 * MILLISECONDS_PER_MINUTE;

/** Priority passed to `UnityDialog` for the pause-timeout warning dialog. */
export const PAUSE_TIMEOUT_DIALOG_PRIORITY = 1;

/**
 * Idle time before the generic inactivity OSD (e.g. Search, and future
 * pages) is shown (15 minutes). Unlike the playback inactivity OSD,
 * generic pages have no exit timeout — the OSD is dismissed only by user
 * input.
 */
export const INACTIVITY_OSD_TIMEOUT_MS = 15 * MILLISECONDS_PER_MINUTE;

/**
 * Priority passed to the shared `osd` slice for the generic inactivity OSD.
 * Same contract as {@link PLAYBACK_INACTIVITY_OSD_PRIORITY}.
 */
export const INACTIVITY_OSD_PRIORITY = 900;

/**
 * Auto-dismiss timeout for a playback error dialog (5 minutes).
 *
 * Longer than `UnityDialogContainer`'s 60 s default: a playback error is a dead
 * end the user may walk away from, and kaveri gives the same class of overlay
 * 5 minutes (`OVERLAY_TIMEOUT`) before it hides and leaves the player.
 */
export const PLAYBACK_ERROR_OVERLAY_TIMEOUT_MS =
  5 * 60 * MILLISECONDS_IN_A_SECOND;

/** Continuous live watch required before it counts as "watched" (5 min). */
export const LIVE_WATCH_HISTORY_DELAY_MS: number = 5 * 60 * 1000;

/** Upper bound of the random jitter added after the 5-min threshold (3 min). */
export const LIVE_WATCH_HISTORY_JITTER_MAX_MS: number = 3 * 60 * 1000;
